import { type SubmissionResult, getFormProps, getInputProps, useForm  } from "@conform-to/react"
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useEffect, useState } from 'react'
import { Form, Link, useLocation, useNavigate } from "react-router";
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from "zod"
import { ErrorList, Field } from '#app/components/forms.tsx'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '#app/components/ui/dialog.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { useLanguage } from '#app/contexts/language-context.tsx';
import { useIsPending } from "#app/utils/misc.tsx"
import { NameSchema } from "#app/utils/user-validation.ts"
import { useOptionalUser } from "#app/utils/user.ts"
import { Button } from "./ui/button"
import { Icon } from "./ui/icon"

export type SaveSearchFilters = {
    saleType:string,
    area:string,
    city:string,
    propType:string,
    priceMin:string | null,
    priceMax:string | null,
    bedsMin:string | null,
    bedsMax:string | null,
    bathsMin:string | null,
    bathsMax:string | null,
    sorting:string | null,
    added:string | null,
    heating:string[],
    condition:string[],
    extras:string[],
};


export const SaveSearchSchema = z.object({
	name: NameSchema,
	filtersState: z.string({ required_error: 'Filters are required' })
})

export function SaveSearchModal({actionData, filtersState, showModal, showButton=true, handleOpen}:{actionData?: {result: SubmissionResult<string[]>}, filtersState:SaveSearchFilters, showModal?:boolean, showButton?:boolean, handleOpen?:any}) {
    const navigate = useNavigate()
	const user = useOptionalUser()
	const location = useLocation()
	const isPending = useIsPending()
	const [saveSearchOpen, setSaveSearchOpen] = useState(false)
	let { t, currentLang } = useLanguage();

    const [form, fields] = useForm({
		id: 'save-search-form',
		constraint: getZodConstraint(SaveSearchSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SaveSearchSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	// TODO: remove this when implementing save search func for map. create its own component.
	useEffect(() => {
		if (showModal != undefined && showModal != saveSearchOpen) {
			// TODO: promise needs to be awaited (outside of useeffect)
			void handleSaveDialogChange(showModal)
		}
	}, [showModal])

	const handleSaveDialogChange = async (open:boolean) => {
		if (open && !user) {
			const params = new URLSearchParams({
				redirectTo: location.pathname + location.search,
			})

			await navigate({
				pathname: `/${currentLang}/login`,
				search: '?' + params.toString(),
			})

			return
		}
		
		setSaveSearchOpen(open)
		// TODO: remove this when implementing save search func for map. create its own component.
		if (handleOpen) {
			handleOpen(open)
		}
	}


	return (
		<Dialog open={saveSearchOpen} onOpenChange={handleSaveDialogChange}>
			{showButton && 
				<DialogTrigger asChild>
					<Button>
						<Icon name="heart-solid" size="md" className="mr-1" />
						{t('Actions', 'Save search')}
					</Button>
				</DialogTrigger>
			}
			
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>{t('Actions', 'Save search')}</DialogTitle>
					<DialogDescription>
						{t('Actions', 'Provide a name for your search and save. Next time pick up from where you left off!')}
					</DialogDescription>
				</DialogHeader>
				{actionData?.result.status === 'success' ? (
					<>
						<div className="w-full">
							<p className="text-base font-semibold">
								<Icon name="check-circle" size="lg" className="mr-1" />
								{t('Actions', 'Success! Your search \"')}
								{actionData?.result.initialValue?.name as string}
								{t('Actions', '\" has been saved.')}
							</p>
							{/* <p className='text-sm italic'>You can access your saved searches from your account.</p> */}
						</div>
						<div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
							<Button variant="secondary" asChild>
								<Link to={`/${currentLang}/me/favourites/searches`}>{t('Actions', 'Manage saved searches')}</Link>
							</Button>
							<Button autoFocus={true} onClick={() => setSaveSearchOpen(false)}>
								{t('Actions', 'Back to search')}
							</Button>
						</div>
					</>
				) : (
					<div className="grid gap-4 py-2">
						<Form method="POST" {...getFormProps(form)}>
							<HoneypotInputs />
							<Field
								labelProps={{
									htmlFor: fields.name.id,
									children: t('Actions', 'Name'),
								}}
								inputProps={{
									...getInputProps(fields.name, { type: 'text' }),
									autoFocus: true,
								}}
								errors={fields.name.errors}
							/>
							<ErrorList errors={form.errors} id={form.errorId} />
							<input
								type="hidden"
								name="filtersState"
								value={JSON.stringify(filtersState)}
							/>

							<DialogFooter>
								<Button
									variant="secondary"
									onClick={() => setSaveSearchOpen(false)}
								>
									{t('Actions', 'Cancel')}
								</Button>
								<StatusButton
									className="w-full"
									status={isPending ? 'pending' : (form.status ?? 'idle')}
									type="submit"
									disabled={isPending}
								>
									{t('Actions', 'Save search')}
								</StatusButton>
							</DialogFooter>
						</Form>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
